import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Table2, Check } from 'lucide-react';
import { onboardingApi } from '../../services/api';
import type { OnboardingState, TableInfo } from '../../types';

interface Props {
  state: OnboardingState;
  setSchema: (s: string) => void;
  setTables: (t: TableInfo[]) => void;
  setColumns: (c: Record<string, any>) => void;
  onNext: () => void;
  onBack: () => void;
}

const TableSelector: React.FC<Props> = ({
  state,
  setSchema,
  setTables,
  setColumns,
  onNext,
  onBack,
}) => {
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setLocalTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);

  /* Fetch schemas on mount (ref guard prevents StrictMode double-fire) */
  const schemasFetched = useRef(false);
  useEffect(() => {
    if (schemasFetched.current) return;
    schemasFetched.current = true;
    const fetchSchemas = async () => {
      setLoading(true);
      try {
        const res = await onboardingApi.listSchemas(state.connection as any);
        setSchemas(res.data.payload || []);
      } catch {
        setSchemas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSchemas();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Fetch tables when schema is selected */
  const handleSchemaSelect = async (schema: string) => {
    setSchema(schema);
    setLoadingTables(true);
    try {
      const res = await onboardingApi.listTables({ ...state.connection, schema });
      setLocalTables(res.data.payload || []);
    } catch {
      setLocalTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const isSelected = (t: TableInfo) =>
    state.selectedTables.some(
      (s) => s.schema_name === t.schema_name && s.table_name === t.table_name,
    );

  const toggleTable = (t: TableInfo) => {
    if (isSelected(t)) {
      setTables(
        state.selectedTables.filter(
          (s) => !(s.schema_name === t.schema_name && s.table_name === t.table_name),
        ),
      );
    } else {
      setTables([...state.selectedTables, t]);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Select tables to monitor</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Schema list */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3">
            Schemas
          </h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-cargo-accent animate-spin" />
            </div>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {schemas.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSchemaSelect(s)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    state.selectedSchema === s
                      ? 'bg-cargo-accent/15 text-cargo-accent'
                      : 'text-cargo-text hover:bg-cargo-hover/50'
                  }`}
                >
                  {s}
                </button>
              ))}
              {schemas.length === 0 && !loading && (
                <p className="text-cargo-muted text-sm text-center py-4">No schemas found</p>
              )}
            </div>
          )}
        </div>

        {/* Table list */}
        <div className="md:col-span-2 bg-cargo-surface border border-cargo-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3">
            Tables {state.selectedTables.length > 0 && `(${state.selectedTables.length} selected)`}
          </h3>
          {loadingTables ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-cargo-accent animate-spin" />
            </div>
          ) : !state.selectedSchema ? (
            <p className="text-cargo-muted text-sm text-center py-8">
              Select a schema to see tables
            </p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {tables.map((t) => {
                const sel = isSelected(t);
                return (
                  <button
                    key={`${t.schema_name}.${t.table_name}`}
                    onClick={() => toggleTable(t)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      sel
                        ? 'bg-cargo-accent/15 text-cargo-accent'
                        : 'text-cargo-text hover:bg-cargo-hover/50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                        sel ? 'bg-cargo-accent border-cargo-accent' : 'border-cargo-border'
                      }`}
                    >
                      {sel && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <Table2 className="w-4 h-4 flex-shrink-0 text-cargo-muted" />
                    <span>{t.table_name}</span>
                  </button>
                );
              })}
              {tables.length === 0 && (
                <p className="text-cargo-muted text-sm text-center py-4">No tables found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={state.selectedTables.length === 0}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TableSelector;
